<script setup>
  import { ref, onBeforeMount, watch, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useRoute } from 'vue-router';
  import { useLayout } from '@/layout/composables/layout';
  import { useAuthStore } from '@/stores/auth';
  const authStore = useAuthStore();
  const router = useRouter();
  const isAuthorized = computed(() => {
    if (!props.item.to) return true; // to 속성이 없으면 기본적으로 허용

    let route;
    try {
      route = router.resolve(props.item.to);
    } catch (error) {
      console.warn(`Invalid route: ${props.item.to}`);
      return true; // 유효하지 않은 라우트는 기본적으로 허용
    }
    if (!route.meta || !route.meta.permission) return true;
    return authStore.hasAnyPermission(route.meta.permission);
  });

  const route = useRoute();

  const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } =
    useLayout();

  const props = defineProps({
    item: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
    root: {
      type: Boolean,
      default: true,
    },
    parentItemKey: {
      type: String,
      default: null,
    },
  });

  const isActiveMenu = ref(false);
  const itemKey = ref(null);

  onBeforeMount(() => {
    itemKey.value = props.parentItemKey
      ? props.parentItemKey + '-' + props.index
      : String(props.index);

    const activeItem = layoutState.activeMenuItem;

    isActiveMenu.value =
      activeItem === itemKey.value || activeItem
        ? activeItem.startsWith(itemKey.value + '-')
        : false;
  });

  watch(
    () => layoutConfig.activeMenuItem.value,
    (newVal) => {
      isActiveMenu.value =
        newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
    },
  );

  const initializeActiveState = () => {
    isActiveMenu.value = checkActiveRoute(props.item);
  };

  watch(
    () => route.path,
    () => {
      initializeActiveState();
    },
  );

  onMounted(() => {
    initializeActiveState();
  });

  const itemClick = (event, item) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    // 권한 체크: 하위 메뉴가 없는 경우에만 수행
    if (!isAuthorized.value && !item.items) {
      event.preventDefault();
      alert('이 기능에 대한 접근 권한이 없습니다.!');
      return;
    }

    // 메뉴 열림/닫힘 토글
    if (item.items) {
      isActiveMenu.value = !isActiveMenu.value;
      event.preventDefault();
    } else {
      // 링크로 이동하거나 명령 실행
      if (item.command) {
        item.command({ originalEvent: event, item: item });
      }

      // 모바일 메뉴의 경우 메뉴 닫기
      const { overlayMenuActive, staticMenuMobileActive } = layoutState;
      if (
        (item.to || item.url) &&
        (staticMenuMobileActive.value || overlayMenuActive.value)
      ) {
        onMenuToggle();
      }
    }
  };

  // const itemClick = (event, item) => {
  //   if (item.disabled) {
  //     event.preventDefault();
  //     return;
  //   }
  //   // 하위 메뉴가 없는 경우에만 권한 체크를 수행
  //   if (!isAuthorized.value && !item.items) {
  //     event.preventDefault();
  //     alert('이 기능에 대한 접근 권한이 없습니다.');
  //     return;
  //   }

  //   // 메뉴 열림/닫힘 상태를 토글
  //   if (item.items) {
  //     isActiveMenu.value = !isActiveMenu.value;
  //   }

  //   // 링크로 이동하거나 명령을 실행
  //   if (item.command) {
  //     item.command({ originalEvent: event, item: item });
  //   }

  //   const { overlayMenuActive, staticMenuMobileActive } = layoutState;

  //   // if (
  //   //   (item.to || item.url) &&
  //   //   (staticMenuMobileActive.value || overlayMenuActive.value)
  //   // ) {
  //   //   onMenuToggle();
  //   // }
  //   // 하위 메뉴가 있는 경우에는 메뉴를 닫지 않음
  //   if (
  //     (item.to || item.url) &&
  //     !item.items &&
  //     (staticMenuMobileActive.value || overlayMenuActive.value)
  //   ) {
  //     onMenuToggle();
  //   }

  //   // const foundItemKey = item.items
  //   //   ? isActiveMenu.value
  //   //     ? props.parentItemKey
  //   //     : itemKey
  //   //   : itemKey.value;

  //   // setActiveMenuItem(foundItemKey);

  //   // 하위 메뉴가 있는 경우 이벤트의 기본 동작을 방지
  //   if (item.items) {
  //     event.preventDefault();
  //   }
  // };

  const checkActiveRoute = (item) => {
    if (item.to && route.path === item.to) {
      return true;
    }
    if (item.items) {
      return item.items.some((childItem) => checkActiveRoute(childItem));
    }
    return false;
  };
</script>

<template>
  <li
    v-show="item.authorized !== false"
    :class="{
      'layout-root-menuitem': root,
      'active-menuitem': isActiveMenu,
      unauthorized: !isAuthorized,
    }"
  >
    <div
      v-if="root && item.visible !== false"
      class="layout-menuitem-root-text"
    >
      {{ item.label }}
    </div>
    <a
      v-if="(!item.to || item.items) && item.visible !== false"
      :href="item.url"
      @click="itemClick($event, item, index)"
      :class="[item.class, { unauthorized: !isAuthorized }]"
      :target="item.target"
      tabindex="0"
    >
      <i
        :class="item.icon"
        class="layout-menuitem-icon"
      ></i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
        v-if="item.items"
      ></i>
    </a>
    <router-link
      v-if="item.to && !item.items && item.visible !== false"
      @click="itemClick($event, item, index)"
      :class="[
        item.class,
        {
          'active-route': checkActiveRoute(item),
          unauthorized: !isAuthorized,
        },
      ]"
      tabindex="0"
      :to="item.to"
    >
      <i
        :class="item.icon"
        class="layout-menuitem-icon"
      ></i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
        v-if="item.items"
      ></i>
    </router-link>
    <Transition
      v-if="item.items && item.visible !== false"
      name="layout-submenu"
    >
      <ul
        v-show="root ? true : isActiveMenu"
        class="layout-submenu"
      >
        <app-menu-item
          v-for="(child, i) in item.items"
          :key="child"
          :index="i"
          :item="child"
          :parentItemKey="itemKey"
          :root="false"
        ></app-menu-item>
      </ul>
    </Transition>
  </li>
</template>

<style lang="scss" scoped>
  li {
    margin: 0;
    padding: 0;
  }

  .unauthorized {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
